/**
 * PriorityQueue — the ranked priority-action list (the BPA "Top six to fix
 * first") as a Carbon DataTable (DESIGN.md "Data table"): one row per action
 * in rank order, with its severity (glyph and word; Coral for critical
 * only), the action title over a helper line (product, scope, frameworks),
 * the estimated Guardian Score lift as a neutral tag, and a Microsoft Learn
 * link. No dropcap ranks: the order is the rank, and the table says so.
 *
 * Rows activate on click and on Enter or Space when `onItemClick` is set.
 * The props are unchanged; `columns` is accepted for compatibility (the
 * table is one list at every width), and `ariaLabel`, `title`,
 * `description` and `footer` are new and optional.
 */

import type { ReactNode } from 'react'
import type { PriorityAction, Severity } from '@/lib/posture-brief'
import ScoreDeltaChip from '@/components/Dashboard/v2/ScoreDeltaChip'
import LearnMoreLink from '@/components/Dashboard/v2/LearnMoreLink'
import { DataTable, type DataColumn } from '@/components/carbon/DataTable'
import { SeverityGlyph } from '@/components/carbon/SeverityGlyph'
import type { SeverityLevel } from '@/components/carbon/types'

const LEVEL: Record<Severity, SeverityLevel> = {
  crit: 'crit',
  high: 'high',
  med: 'med',
  low: 'low',
  routine: 'low',
}

interface PriorityQueueProps {
  actions: PriorityAction[]
  /** Kept for compatibility; the Carbon table is a single list at every width. */
  columns?: 1 | 2
  /** Rendered when actions[] is empty. */
  emptyState?: ReactNode
  onItemClick?: (action: PriorityAction) => void
  /** The table's accessible name when it has no `title`. Default "Priority actions". */
  ariaLabel?: string
  /** A visible table title (also its accessible name). */
  title?: string
  /** One line under the title. */
  description?: ReactNode
  /** The footer line under the rows (a link to the full list, say). */
  footer?: ReactNode
}

export default function PriorityQueue({
  actions,
  emptyState,
  onItemClick,
  ariaLabel = 'Priority actions',
  title,
  description,
  footer,
}: PriorityQueueProps) {
  const columns: DataColumn<PriorityAction>[] = [
    {
      id: 'rank',
      header: <span className="sr-only">Rank</span>,
      width: '3rem',
      nowrap: true,
      cell: (action) => (
        <span className="tabular-nums text-text-helper">{actions.indexOf(action) + 1}</span>
      ),
    },
    {
      id: 'severity',
      header: 'Severity',
      nowrap: true,
      width: '8rem',
      cell: (action) => <SeverityGlyph level={LEVEL[action.severity] ?? 'med'} />,
    },
    {
      id: 'action',
      header: 'Action',
      primary: true,
      cell: (action) => {
        const meta = [
          action.meta,
          typeof action.scope === 'number' && action.scope > 0 ? `${action.scope} in scope` : null,
          typeof action.frameworksAffected === 'number' && action.frameworksAffected > 0
            ? `${action.frameworksAffected} framework${action.frameworksAffected !== 1 ? 's' : ''}`
            : null,
        ].filter(Boolean)
        return (
          <>
            <b className="block font-semibold text-text-primary">{action.title}</b>
            {meta.length > 0 && <small className="mt-0.5 block text-helper text-text-helper">{meta.join(' · ')}</small>}
          </>
        )
      },
    },
    {
      id: 'lift',
      header: 'Score lift',
      align: 'end',
      nowrap: true,
      cell: (action) => (
        <span className="inline-flex items-center gap-1">
          <ScoreDeltaChip delta={action.guardianScoreDelta} />
          <LearnMoreLink url={action.learnMoreUrl} size="md" />
        </span>
      ),
    },
  ]

  return (
    <DataTable<PriorityAction>
      title={title}
      description={description}
      footer={footer}
      aria-label={ariaLabel}
      columns={columns}
      rows={actions}
      rowId={(a) => a.id}
      rowLabel={(a) => a.title}
      onRowActivate={onItemClick}
      empty={emptyState ?? 'Nothing to review.'}
    />
  )
}
